/**
 * Engine config panel (§8.1 Config tab): view the active payroll_engine_config
 * version, edit as a NEW version (SuperAdmin, mandatory reason), full history.
 * Inputs are typed from the current value (boolean → switch, number → number
 * input, string → text). live_enabled is the Phase 9 master interlock.
 */

import React, { useMemo, useState } from 'react';
import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Alert,
  AlertIcon,
  Badge,
  Box,
  Button,
  Code,
  Flex,
  FormControl,
  FormLabel,
  Input,
  Spinner,
  Switch,
  Table,
  Tbody,
  Td,
  Text,
  Textarea,
  Th,
  Thead,
  Tr,
  useToast,
} from '@chakra-ui/react';
import { payrollApi } from '../api';

const DANGER_KEYS = new Set(['live_enabled', 'schedule_enabled']);
const HELP = {
  live_enabled: 'Master interlock — live runs refuse to start while false (Phase 9).',
  schedule_enabled: 'Master switch for the Schedule tab dispatcher — automatic runs only fire while true ("Run now" works regardless).',
  bp_drift_threshold: 'BP_DRIFT flag when |sheet BP − computed BP| exceeds this ($).',
  backtest_tolerance: 'Backtest match if |computed − actual| ≤ this ($).',
  backtest_row_count: 'Default rows scanned by a backtest.',
  tier_count_scope: 'CD tier month-count scope (company = all rows, per v2.9).',
  mirror_preview: 'Also write dry-run previews to the Payroll Preview sheet tab (Phase 9).',
  mirror_backtest: 'Also write backtests to the Payroll Backtest sheet tab (Phase 9).',
  live_preview_max_age_minutes: 'Live runs require a dry run newer than this (Phase 9 interlock).',
};

function valueToInput(v) { return v === null || v === undefined ? '' : String(v); }

function parseDraftValue(original, raw) {
  if (typeof original === 'boolean') return !!raw;
  if (typeof original === 'number') {
    const n = Number(raw);
    if (raw === '' || !Number.isFinite(n)) throw new Error('not a number');
    return n;
  }
  return String(raw);
}

export default function ConfigPanel({ isSuperAdmin }) {
  const toast = useToast();
  const [state, setState] = useState(null); // { current, history }
  const [loadError, setLoadError] = useState(null);
  const [draft, setDraft] = useState(null); // { values: {k: raw}, reason }
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoadError(null);
    try {
      const res = await payrollApi.getConfig();
      setState(res);
    } catch (err) {
      setLoadError(err.message);
    }
  };
  React.useEffect(() => { load(); }, []);

  // schedule_enabled is synthesized (false) until first saved, so the switch
  // is flippable even before the key physically exists in the config row.
  const config = useMemo(
    () => (state && state.current ? { schedule_enabled: false, ...state.current.config } : null),
    [state],
  );

  const changes = useMemo(() => {
    if (!draft || !config) return [];
    const out = [];
    Object.keys(config).forEach((k) => {
      try {
        const next = parseDraftValue(config[k], draft.values[k]);
        if (JSON.stringify(next) !== JSON.stringify(config[k])) out.push({ key: k, from: config[k], to: next });
      } catch (e) {
        out.push({ key: k, error: true });
      }
    });
    return out;
  }, [draft, config]);

  const startEdit = () => {
    const values = {};
    Object.entries(config).forEach(([k, v]) => { values[k] = typeof v === 'boolean' ? v : valueToInput(v); });
    setDraft({ values, reason: '' });
  };

  const save = async () => {
    if (changes.some((c) => c.error)) {
      toast({ title: 'Fix invalid numeric values first', status: 'warning', duration: 4000 });
      return;
    }
    if (!changes.length) {
      toast({ title: 'No changes to save', status: 'info', duration: 3000 });
      return;
    }
    if (!draft.reason.trim()) {
      toast({ title: 'A change reason is required', status: 'warning', duration: 4000 });
      return;
    }
    setSaving(true);
    try {
      const next = { ...config };
      changes.forEach((c) => { next[c.key] = c.to; });
      const { version } = await payrollApi.setConfig({ config: next, reason: draft.reason.trim() });
      toast({ title: `Saved as config v${version}`, status: 'success', duration: 4000 });
      setDraft(null);
      await load();
    } catch (err) {
      toast({ title: 'Save failed', description: err.message, status: 'error', duration: 10000, isClosable: true });
    } finally {
      setSaving(false);
    }
  };

  if (loadError) return <Alert status="error" borderRadius="md"><AlertIcon />{loadError}<Button ml={3} size="sm" onClick={load}>Retry</Button></Alert>;
  if (!state) return <Flex justify="center" py={10}><Spinner /></Flex>;
  if (!config) return <Alert status="warning" borderRadius="md"><AlertIcon />No active engine config found.</Alert>;

  return (
    <Box>
      <Flex align="center" gap={3} mb={3} wrap="wrap">
        <Text fontWeight="semibold">
          Active: v{state.current.version}
          <Badge ml={2} colorScheme={config.live_enabled ? 'red' : 'green'}>
            live_enabled: {String(config.live_enabled)}
          </Badge>
          <Badge ml={2} colorScheme={config.schedule_enabled ? 'red' : 'green'}>
            schedule_enabled: {String(config.schedule_enabled)}
          </Badge>
        </Text>
        <Box flex="1" />
        {!draft && (
          <Button size="sm" colorScheme="purple" onClick={startEdit} isDisabled={!isSuperAdmin}
            title={isSuperAdmin ? undefined : 'SuperAdmin only'}>
            Edit (new version)
          </Button>
        )}
        {draft && (
          <>
            <Button size="sm" variant="ghost" onClick={() => setDraft(null)}>Cancel</Button>
            <Button size="sm" colorScheme="purple" onClick={save} isLoading={saving}
              isDisabled={!changes.length || changes.some((c) => c.error)}>
              Save as v{state.current.version + 1}
            </Button>
          </>
        )}
      </Flex>

      {draft && (
        <Box mb={3}>
          {changes.length > 0 && (
            <Alert status="warning" borderRadius="md" mb={2} fontSize="sm">
              <AlertIcon />
              <Box>
                {changes.map((c) => (
                  <Text key={c.key} fontSize="xs">
                    {c.error
                      ? <b style={{ color: 'var(--chakra-colors-red-400)' }}>{c.key}: invalid number</b>
                      : <><b>{c.key}</b>: <Code fontSize="xs">{String(c.from)}</Code> → <Code fontSize="xs">{String(c.to)}</Code></>}
                  </Text>
                ))}
              </Box>
            </Alert>
          )}
          <FormControl isRequired>
            <FormLabel fontSize="sm">Change reason (audited)</FormLabel>
            <Textarea size="sm" rows={2} value={draft.reason}
              onChange={(e) => setDraft((s) => ({ ...s, reason: e.target.value }))} />
          </FormControl>
        </Box>
      )}

      <Box overflowX="auto" borderWidth="1px" borderRadius="md" mb={4}>
        <Table size="sm">
          <Thead><Tr><Th>Key</Th><Th>Value</Th><Th>Notes</Th></Tr></Thead>
          <Tbody>
            {Object.entries(config).map(([k, v]) => (
              <Tr key={k} bg={DANGER_KEYS.has(k) ? 'rgba(229,62,62,0.06)' : undefined}>
                <Td whiteSpace="nowrap" fontFamily="mono" fontSize="xs">
                  {k}{DANGER_KEYS.has(k) && <Badge ml={1} colorScheme="red">danger</Badge>}
                </Td>
                <Td>
                  {!draft && <Code fontSize="xs">{String(v)}</Code>}
                  {draft && typeof v === 'boolean' && (
                    <Switch size="sm" colorScheme={DANGER_KEYS.has(k) ? 'red' : 'purple'}
                      isChecked={!!draft.values[k]}
                      onChange={(e) => setDraft((s) => ({ ...s, values: { ...s.values, [k]: e.target.checked } }))} />
                  )}
                  {draft && typeof v !== 'boolean' && (
                    <Input size="xs" maxW="420px" fontFamily="mono" value={draft.values[k]}
                      onChange={(e) => setDraft((s) => ({ ...s, values: { ...s.values, [k]: e.target.value } }))} />
                  )}
                </Td>
                <Td fontSize="xs" color="gray.500">{HELP[k] || ''}</Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      </Box>

      <Accordion allowToggle>
        <AccordionItem>
          <AccordionButton>
            <Box flex="1" textAlign="left" fontSize="sm" fontWeight="semibold">
              Version history ({state.history.length})
            </Box>
            <AccordionIcon />
          </AccordionButton>
          <AccordionPanel>
            {state.history.map((h) => (
              <Box key={h.version} py={2} borderBottomWidth="1px" _last={{ borderBottomWidth: 0 }}>
                <Text fontSize="sm">
                  <b>v{h.version}</b>{h.active && <Badge ml={2} colorScheme="green">active</Badge>}
                  <Text as="span" color="gray.500" ml={2} fontSize="xs">
                    {new Date(h.created_at).toLocaleString()} · {h.changed_by_email || 'seed'}
                  </Text>
                </Text>
                <Text fontSize="xs" color="gray.500">{h.change_reason}</Text>
              </Box>
            ))}
          </AccordionPanel>
        </AccordionItem>
      </Accordion>
    </Box>
  );
}
